import { useState, useEffect } from "react";
import { Plus, Search, ReceiptIndianRupee, X, Check, Layers, ClipboardCheck, Send, Save, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { apiGet, apiPost, fmtDate, isPeriodClosedError } from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { SupplierLookup } from "@/components/SupplierLookup";

const COST_TYPES = [
  "Freight", "Insurance", "Loading Charges", "Unloading Charges", "Customs Clearance",
  "Port / Terminal Charges", "Basic Customs Duty (BCD)", "Social Welfare Surcharge (SWS)",
  "Customs Cess", "Import IGST", "Other",
];

const STATUS_CHIP = {
  draft: "bg-slate-100 text-slate-600",
  posted: "bg-green-100 text-green-700",
  allocated: "bg-blue-100 text-blue-700",
  error: "bg-red-100 text-red-700",
  sap_draft_pending_review: "bg-amber-100 text-amber-800",
  sap_draft_submitting: "bg-sky-100 text-sky-800",
  sap_post_submitting: "bg-sky-100 text-sky-800",
};

export default function FreightInvoices() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const showForm = location.pathname === "/supplementary-invoices/new" || searchParams.get("action") === "new";
  const [bp, setBp] = useState(null);
  const [invNumber, setInvNumber] = useState("");
  const [invDate, setInvDate] = useState("");
  const [postingDate, setPostingDate] = useState("");
  const [postingDateTouched, setPostingDateTouched] = useState(false);
  const [site, setSite] = useState("");
  const [sites, setSites] = useState([]);
  const [sapPlants, setSapPlants] = useState([]);
  const [plantCode, setPlantCode] = useState("");
  const [siteError, setSiteError] = useState("");
  const [costLines, setCostLines] = useState([{ cost_type: "Freight", amount: "", description: "", hsn_sac_code: "996511" }]);
  const [supplierBillTax, setSupplierBillTax] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [sapBusyId, setSapBusyId] = useState("");

  const load = () => {
    setLoading(true);
    apiGet("/freight-invoices")
      .then((r) => setInvoices(r.items || []))
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);
  useEffect(() => {
    if (location.pathname === "/supplementary-invoices" && searchParams.get("action") === "new") {
      navigate("/supplementary-invoices/new", { replace: true });
    }
  }, [location.pathname, searchParams, navigate]);
  useEffect(() => {
    apiGet("/lookups/sites").then((data) => setSites(data.items)).catch((err) => setSiteError(err.message));
    apiGet("/lookups/sap-plants").then((data) => setSapPlants(data.items || [])).catch((err) => setSiteError(err.message));
  }, []);

  // Posting Date defaults to the Invoice Date (not today) — matches finance's
  // expectation that absent an explicit override, posting follows the
  // supplier's own invoice date. Stops auto-following once user edits it.
  useEffect(() => {
    if (!postingDateTouched) setPostingDate(invDate);
  }, [invDate, postingDateTouched]);

  const setEntryMode = (creating) => navigate(creating ? "/supplementary-invoices/new" : "/supplementary-invoices");
  const closeForm = () => setEntryMode(false);

  const addLine = () => setCostLines((l) => [...l, { cost_type: "Freight", amount: "", description: "", hsn_sac_code: "996511" }]);
  const removeLine = (i) => setCostLines((l) => l.filter((_, idx) => idx !== i));
  const updateLine = (i, field, val) =>
    setCostLines((l) => l.map((r, idx) => idx === i ? { ...r, [field]: val } : r));

  const totalCost = costLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);

  const runSapAction = async (invoice, action) => {
    setSapBusyId(`${invoice.fi_id}-${action}`);
    try {
      if (action === "prepare") {
        await apiPost(`/freight-invoices/${invoice.fi_id}/prepare-sap-draft`, {});
        toast.success("SAP draft saved. Review it before posting.");
      } else if (action === "review") {
        const review = await apiGet(`/freight-invoices/${invoice.fi_id}/sap-review`);
        const openExceptions = (review.exceptions || []).filter((item) => item.status_code === "1").length;
        toast.success(openExceptions ? `SAP review returned ${openExceptions} open exception(s).` : "SAP draft is ready for your explicit posting action.");
      } else {
        await apiPost(`/freight-invoices/${invoice.fi_id}/finalize-sap-post`, {});
        toast.success("Supplier invoice posted in SAP.");
      }
      load();
    } catch (err) {
      toast.error(err.message || "SAP action failed");
      load();
    } finally {
      setSapBusyId("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bp) { toast.error("Please select a business partner"); return; }
    if (!invNumber) { toast.error("Invoice number is required"); return; }
    if (!sites.some((option) => option.id === site)) { toast.error("Please select a plant/site"); return; }
    if (!sapPlants.some((option) => option.code === plantCode)) { toast.error("Please select a SAP Plant"); return; }
    if (costLines.some((l) => !Number.isFinite(Number(l.amount)) || Number(l.amount) <= 0)) {
      toast.error("All cost lines must have a positive amount"); return;
    }
    if (costLines.some((l) => !l.hsn_sac_code?.trim())) { toast.error("SAC/HSN is required for every charge line"); return; }
    if (!Number.isFinite(Number(supplierBillTax)) || Number(supplierBillTax) < 0) { toast.error("Enter the GST amount shown on the supplier bill"); return; }
    setSaving(true);
    try {
      await apiPost("/freight-invoices", {
        vendor_id: bp.id, vendor_name: bp.name, vendor_gstin: bp.gstin,
        invoice_number: invNumber, invoice_date: invDate, posting_date: postingDate || null, site, plant_code: plantCode,
        supplier_bill_tax_amount: parseFloat(supplierBillTax),
        invoicing_unit: `${site}-FIN`,
        cost_lines: costLines.map((l) => ({ ...l, amount: parseFloat(l.amount) })),
        created_by: user?.email,
      });
      toast.success("Supplementary invoice draft saved. Not posted to SAP.");
      closeForm();
      setBp(null); setInvNumber(""); setInvDate(""); setPostingDate(""); setPostingDateTouched(false); setPlantCode("");
      setCostLines([{ cost_type: "Freight", amount: "", description: "", hsn_sac_code: "996511" }]); setSupplierBillTax("");
      load();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const filtered = invoices.filter(
    (inv) =>
      inv.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
      inv.vendor_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-heading font-bold text-slate-900" data-testid="supplementary-invoice-page-heading">Supplementary Cost Invoices</h2>
          <p className="text-[11px] text-slate-500 mt-0.5" data-testid="supplementary-invoice-page-description">Freight, loading, customs and other post-purchase supplier costs</p>
        </div>
        {!showForm && (
          <Button
            onClick={() => setEntryMode(true)}
            className="gap-2 text-sm h-9"
            style={{ backgroundColor: "#0A838A", color: "#fff" }}
            data-testid="add-supplementary-invoice-button"
          >
            <Plus size={14} /> Add Supplementary Invoice
          </Button>
        )}
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="bg-white border border-slate-200 p-4 sm:p-5 space-y-4" data-testid="freight-invoice-form">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-[14px] font-bold text-slate-900" data-testid="add-supplementary-invoice-heading">Add Supplementary Invoice</h3>
            <button onClick={closeForm} className="text-slate-400 hover:text-slate-600" data-testid="close-supplementary-invoice-form-button">
              <X size={16} />
            </button>
          </div>

          <p role="status" className="border-l-2 border-teal-600 bg-teal-50 p-3 text-xs text-teal-950" data-testid="supplementary-sap-posting-status">Save locally first. Eligible freight/loading invoices use SAP’s live supplier and coding checks, then follow Save SAP Draft → Review → Post. Taxable value is capped at ₹100.00; SAP-determined tax is retained.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* BP Search */}
            <div className="space-y-1">
              <label className="text-[12px] font-bold text-slate-700">Supplier / Customs Broker *</label>
              <SupplierLookup value={bp} onChange={setBp} />
              {bp?.gstin && (
                <p className="text-[11px] text-slate-400 font-mono">GSTIN: {bp.gstin}</p>
              )}
            </div>

            {/* Invoice details row */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
              <div className="space-y-1">
                <label className="text-[12px] font-bold text-slate-700">Invoice Number *</label>
                <Input value={invNumber} onChange={(e) => setInvNumber(e.target.value)}
                  placeholder="e.g. DHL/2026/0451" className="h-9 text-sm" data-testid="freight-inv-number" />
              </div>
              <div className="space-y-1">
                <label className="text-[12px] font-bold text-slate-700">Supplier Bill GST *</label>
                <Input type="number" min="0" step="0.01" value={supplierBillTax} onChange={(e) => setSupplierBillTax(e.target.value)} placeholder="GST amount ₹" className="h-9 text-sm" data-testid="freight-inv-supplier-tax" />
              </div>
              <div className="space-y-1">
                <label className="text-[12px] font-bold text-slate-700">Invoice Date</label>
                <DatePickerInput value={invDate} onChange={setInvDate}
                  className="h-9 text-sm" data-testid="freight-inv-date" />
              </div>
              <div className="space-y-1">
                <label className="text-[12px] font-bold text-slate-700">Posting Date</label>
                <DatePickerInput value={postingDate} onChange={(v) => { setPostingDate(v); setPostingDateTouched(true); }}
                  className="h-9 text-sm" data-testid="freight-inv-posting-date" />
                <p className="text-[10px] text-slate-400">Defaults to Invoice Date; backdate only if that period is open in SAP.</p>
              </div>
              <div className="space-y-1">
                <label className="text-[12px] font-bold text-slate-700">Plant / Site *</label>
                <select value={site} onChange={(e) => setSite(e.target.value)}
                  className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#0A6755]"
                  data-testid="freight-inv-site">
                  <option value="">Select plant / site</option>
                  {[...new Set(sites.map((option) => option.company_code))].map((company) => <optgroup label={company} key={company}>{sites.filter((option) => option.company_code === company).map((option) => <option value={option.id} key={option.id}>{option.id} · {option.company_code}</option>)}</optgroup>)}
                </select>
                <p className="text-[10px] text-slate-500" data-testid="plant-list-source">Approved site configuration · {sites.length} sites</p>
                {site && <p className="font-mono text-[10px] text-slate-500" data-testid="plant-invoicing-unit">{site}-FIN</p>}
                {siteError && <p role="alert" className="text-xs text-red-700" data-testid="plant-list-error">{siteError}</p>}
              </div>
              <div className="space-y-1">
                <label className="text-[12px] font-bold text-slate-700">SAP Plant *</label>
                <select value={plantCode} onChange={(e) => setPlantCode(e.target.value)} className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#0A6755]" data-testid="freight-inv-sap-plant">
                  <option value="">Select SAP Plant</option>
                  {sapPlants.map((plant) => <option value={plant.code} key={plant.code}>{plant.code} · {plant.text}</option>)}
                </select>
                <p className="text-[10px] text-slate-500" data-testid="sap-plant-source">Live SAP Plant value help</p>
              </div>
            </div>

            {/* Cost lines */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-[12px] font-bold text-slate-700">Charge Lines *</label>
                <button type="button" onClick={addLine} data-testid="add-cost-line-button"
                  className="text-[12px] text-[#0A838A] hover:underline font-bold flex items-center gap-1">
                  <Plus size={12} /> Add line
                </button>
              </div>
              {costLines.map((line, i) => (
                <div key={i} className="grid grid-cols-1 gap-2 items-center border-b border-slate-100 pb-3 sm:grid-cols-10">
                  <div className="min-w-0 sm:col-span-2">
                    <select value={line.cost_type} onChange={(e) => updateLine(i, "cost_type", e.target.value)} data-testid={`cost-type-select-${i}`}
                      className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#0A6755]">
                      {COST_TYPES.map((ct) => <option key={ct}>{ct}</option>)}
                    </select>
                  </div>
                  <div className="min-w-0 sm:col-span-2">
                    <Input value={line.hsn_sac_code} onChange={(e) => updateLine(i, "hsn_sac_code", e.target.value)} placeholder="SAC / HSN" className="h-9 text-sm" data-testid={`cost-hsn-sac-input-${i}`} />
                  </div>
                  <div className="min-w-0 sm:col-span-2">
                    <Input type="number" min="0.01" step="0.01" placeholder="Net amount ₹" value={line.amount}
                      onChange={(e) => updateLine(i, "amount", e.target.value)}
                      className="h-9 text-sm" data-testid={`cost-amount-input-${i}`} />
                  </div>
                  <div className="min-w-0 sm:col-span-2">
                    <Input placeholder="Description (optional)" value={line.description}
                      onChange={(e) => updateLine(i, "description", e.target.value)}
                      className="h-9 text-sm" data-testid={`cost-description-input-${i}`} />
                  </div>
                  {costLines.length > 1 && (
                    <button type="button" onClick={() => removeLine(i)} className="text-slate-400 hover:text-red-500" data-testid={`remove-cost-line-button-${i}`}>
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              <div className="flex justify-end">
                <span className="text-[12px] font-semibold text-slate-700" data-testid="supplementary-invoice-total">
                  Net total: ₹{totalCost.toLocaleString("en-IN")}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="submit" disabled={saving} className="gap-2 h-9 text-sm"
                style={{ backgroundColor: "#0A838A", color: "#fff" }} data-testid="save-landed-cost-invoice-button">
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check size={14} />}
                {saving ? "Saving…" : "Save Supplementary Draft"}
              </Button>
              <Button type="button" variant="outline" className="h-9 text-sm" onClick={closeForm} data-testid="cancel-supplementary-invoice-button">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Search + List */}
      {!showForm && (
        <>
          <div className="relative max-w-xs">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input placeholder="Search supplementary invoices…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm" data-testid="supplementary-invoice-search-input" />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="hidden grid-cols-9 px-4 py-2.5 bg-slate-50 border-b border-slate-200 sm:grid">
              {["Invoice #", "Provider", "Site", "Invoice Date", "Posting Date", "SAP Gross", "Charge Lines", "Status", "Action"].map((h) => (
                <span key={h} className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">{h}</span>
              ))}
            </div>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-5 h-5 border-2 border-[#0A6755] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <ReceiptIndianRupee size={18} className="text-slate-400" />
                </div>
                <p className="text-[13px] text-slate-500" data-testid="empty-supplementary-invoices-message">No supplementary invoices yet</p>
                <p className="text-[11px] text-slate-400">Add freight, loading, customs or duty charges to begin</p>
              </div>
            ) : (
              filtered.map((inv, i) => (
                <div key={inv.fi_id || i} className="grid grid-cols-1 gap-2 break-words px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 sm:grid-cols-9" data-testid={`supplementary-invoice-row-${inv.fi_id || i}`}>
                  <span className="min-w-0 text-[13px] font-mono text-[#0A6755] font-semibold" data-testid={`supplementary-invoice-number-${inv.fi_id || i}`}>{inv.invoice_number || inv.fi_id}</span>
                  <span className="min-w-0 text-[13px] text-slate-700" data-testid={`supplementary-invoice-vendor-${inv.fi_id || i}`}>{inv.vendor_name}</span>
                  <span className="text-[13px] text-slate-500" data-testid={`supplementary-invoice-site-${inv.fi_id || i}`}>{inv.site}</span>
                  <span className="text-[13px] font-mono text-slate-500" data-testid={`supplementary-invoice-date-${inv.fi_id || i}`}>{fmtDate(inv.invoice_date)}</span>
                  <span className="text-[13px] font-mono text-slate-500" data-testid={`supplementary-invoice-posting-date-${inv.fi_id || i}`}>{fmtDate(inv.posting_date) !== "—" ? fmtDate(inv.posting_date) : fmtDate(inv.invoice_date)}</span>
                  <span className="min-w-0 text-[13px] font-semibold" data-testid={`supplementary-invoice-amount-${inv.fi_id || i}`}>₹{Number(inv.sap_non_po_grand_total ?? inv.total_cost ?? 0).toLocaleString("en-IN")}</span>
                  <span className="text-[13px] text-slate-500" data-testid={`supplementary-invoice-lines-${inv.fi_id || i}`}>{inv.cost_lines?.length || 0} lines</span>
                  <span data-testid={`supplementary-invoice-status-${inv.fi_id || i}`} className={`self-start text-[11px] px-2 py-0.5 rounded-full w-fit ${STATUS_CHIP[inv.status] || "bg-slate-100 text-slate-600"}`}>
                    {inv.status || "draft"}
                  </span>
                  <div className="flex flex-wrap gap-1.5" data-testid={`supplementary-invoice-actions-${inv.fi_id || i}`}>
                    {(!inv.sap_invoice_id && ["draft", "error"].includes(inv.status)) && (
                      <Button type="button" size="sm" variant="outline" disabled={Boolean(sapBusyId)} onClick={() => runSapAction(inv, "prepare")}
                        className="h-8 gap-1 border-[#0A838A]/30 text-[#076D74] hover:bg-[#E7F5F5]" data-testid={`save-sap-freight-draft-${inv.fi_id || i}`}>
                        <Save size={13} /> {sapBusyId === `${inv.fi_id}-prepare` ? "Saving…" : "Save SAP Draft"}
                      </Button>
                    )}
                    {inv.status === "sap_draft_pending_review" && (
                      <>
                        <Button type="button" size="sm" variant="outline" disabled={Boolean(sapBusyId)} onClick={() => runSapAction(inv, "review")}
                          className="h-8 gap-1" data-testid={`review-sap-freight-draft-${inv.fi_id || i}`}>
                          <ClipboardCheck size={13} /> {sapBusyId === `${inv.fi_id}-review` ? "Reviewing…" : "Review"}
                        </Button>
                        <Button type="button" size="sm" disabled={Boolean(sapBusyId)} onClick={() => runSapAction(inv, "post")}
                          className="h-8 gap-1 bg-[#0A838A] text-white hover:bg-[#076D74]" data-testid={`post-sap-freight-invoice-${inv.fi_id || i}`}>
                          <Send size={13} /> {sapBusyId === `${inv.fi_id}-post` ? "Posting…" : "Post Invoice"}
                        </Button>
                      </>
                    )}
                    <Button type="button" size="sm" variant="outline" disabled={inv.status === "allocated"}
                      onClick={() => navigate(`/cost-allocations?fi_id=${encodeURIComponent(inv.fi_id)}`)}
                      className="h-8 gap-1.5 border-[#0A838A]/30 text-[#076D74] hover:bg-[#E7F5F5]" data-testid={`allocate-supplementary-invoice-${inv.fi_id || i}`}>
                      <Layers size={13} /> Allocate
                    </Button>
                  </div>
                  {inv.sap_error_message && (
                    isPeriodClosedError(inv.sap_error_message) ? (
                      <div className="col-span-full flex items-start gap-2 border border-amber-300 bg-amber-50 p-2 rounded" data-testid={`supplementary-invoice-period-closed-${inv.fi_id || i}`}>
                        <CalendarClock size={14} className="text-amber-700 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[11px] font-semibold text-amber-900">Fiscal Period Closed</p>
                          <p className="text-[11px] text-amber-900">Posting Date falls in a fiscal period SAP has already closed. Contact Finance to choose a different Posting Date on a new draft, or ask your SAP administrator to temporarily reopen the prior period.</p>
                        </div>
                      </div>
                    ) : (
                      <p role="alert" className="col-span-full text-[11px] text-red-700 break-words" data-testid={`supplementary-invoice-error-${inv.fi_id || i}`}>{inv.sap_error_message}</p>
                    )
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
